/**
 * useSleepTimer — auto-stop countdown for sleep sessions.
 *
 * Owns:
 *   - sleepTimerMinutes (selected preset, null = off)
 *   - sleepTimerEnd     (Date.now()-relative absolute timestamp)
 *   - sleepTimerRemaining (seconds; ticked at 1Hz)
 *
 * Caller supplies `onExpire`, which runs once when the countdown hits zero.
 * The hook itself never touches the audio graph — keeping it pure makes the
 * fade/stop policy testable in isolation and lets the component decide
 * whether to fade beats, stop synth ambient, etc.
 *
 * Usage:
 *   const sleep = useSleepTimer({
 *     onExpire: () => { fadeOutAndStop(); },
 *   });
 *   sleep.startSleepTimer(30);   // 30 minutes
 *   sleep.startSleepTimer(null); // cancel
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useSleepTimer({ onExpire } = {}) {
  const [sleepTimerMinutes, setSleepTimerMinutes] = useState(null);
  const [sleepTimerEnd, setSleepTimerEnd] = useState(null);
  const [sleepTimerRemaining, setSleepTimerRemaining] = useState(null);

  // Stash the latest onExpire so the interval closure always sees the
  // current callback without needing it as an effect dependency.
  const onExpireRef = useRef(onExpire);
  useEffect(() => { onExpireRef.current = onExpire; }, [onExpire]);

  const startSleepTimer = useCallback((minutes) => {
    if (!minutes) {
      setSleepTimerMinutes(null);
      setSleepTimerEnd(null);
      setSleepTimerRemaining(null);
      return;
    }
    setSleepTimerMinutes(minutes);
    setSleepTimerEnd(Date.now() + minutes * 60 * 1000);
    setSleepTimerRemaining(minutes * 60);
  }, []);

  // 1Hz countdown.
  useEffect(() => {
    if (!sleepTimerEnd) return;
    const tick = setInterval(() => {
      const remaining = Math.max(0, Math.round((sleepTimerEnd - Date.now()) / 1000));
      setSleepTimerRemaining(remaining);
      if (remaining <= 0) {
        clearInterval(tick);
        try { onExpireRef.current?.(); } catch (e) { console.error('Sleep timer onExpire threw:', e); }
        setSleepTimerMinutes(null);
        setSleepTimerEnd(null);
        setSleepTimerRemaining(null);
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [sleepTimerEnd]);

  return {
    sleepTimerMinutes,
    sleepTimerEnd,
    sleepTimerRemaining,
    startSleepTimer,
  };
}

export default useSleepTimer;
